<template>
  <div class="box working-portfolio">
    <p class="title is-6 has-text-centered">
      {{ portfolio.name }}
      &nbsp;&nbsp;&nbsp;

      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <b-tooltip label="Save as a new portfolio" position="is-right">
              <b-icon
                pack="fas"
                icon="save"
                type="is-primary"
                v-on:click.native="savePortfolio"
              />
            </b-tooltip>
            &nbsp;&nbsp;
            <b-tooltip label="Clear this editor and start over" position="is-right">
              <b-icon
                pack="fas"
                icon="ban"
                type="is-danger"
                v-on:click.native="clear"
              />
            </b-tooltip>

            &nbsp;&nbsp;
            <b-tooltip
              multilined
              position="is-right"
              v-bind:label="
                portfolio.destination.status === 'a' ?
                  'Changes to the portfolio are auto-saved as you make them'
                  : 'Changes are being auto-saved as a draft. Click the Save icon to the left to save this draft as a permanent portfolio with a new name'
                "
            >
              <p class="is-size-7" v-if="saving !== null">
                <em>
                  {{ saving ? 'Saving... ' : 'Saved ' }}
                  {{ !saving && portfolio.destination.status === 'd' ? 'as a draft' : '' }}
                </em>
              </p>
            </b-tooltip>

            &nbsp;&nbsp;&nbsp;
            <div v-show="portfolio.destination.status != 'd'">
            <IRebalSync v-bind:modelPortfolioId="portfolio.destination.id" v-show="$root.advisor.hasFeature('iRebal Integration')"/>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <a
              v-if="portfolio.destination.id"
              v-bind:href="reportUrl"
              target="_blank" rel="noreferrer noopener"
              class="button is-primary is-rounded is-small is-outlined is-active is-pulled-right"
            >
              Generate a Report
            </a>
          </div>
        </div>
      </div>
    </p>
    <div class="block" v-if="!hasWorkingPortfolio">
      <p class="help">
        Choose an existing portfolio to start from, or just start adding holdings from the search results on the left.
      </p>
    </div>
    <TypeaheadPortfolioSearch
      v-if="!hasWorkingPortfolio"
      placeholder="Load a portfolio to start from"
      v-bind:callback="portfolioSelectedCallback"
    />
    <div v-if="portfolio.holdings.length > 0">
      <ValuesChart
        v-if="portfolio.screenedHoldings.length > 0 && (values.a.length > 0 || screens.a.length > 0)"
        v-bind:chartData="chartData"
        v-bind:options="chartOptions"
        v-bind:width="500"
        v-bind:height="150"
      />

      <!-- Overall Alignment: {{ aggregateScore('composite') }}
      <div
        v-for="(valueSet, index) in values.a"
      >
        {{ valueSet.name }} Alignment: {{ aggregateScore(`composite_values_${valueSet.id}`) }}
      </div> -->
      <br><br>
      <strong>
        Holdings
      </strong>
      ({{ portfolio.screenedHoldings.length }}<template v-if="portfolio.holdings.length > portfolio.screenedHoldings.length"> of {{ portfolio.holdings.length }} total holdings</template>)
      <hr>
      <ul class="holdings">
        <li
          v-for="(holding, index) in portfolio.holdings"
          v-bind:class="holdingClass(holding)"
        >
          <p>
            <a href="#_" v-on:click="showDetail(holding, $event)"><strong>{{ holding.symbol }}</strong></a>
            {{ holding.full_name }}
          </p>
          <p><small>{{ holding.benchmark_description }}</small></p>
          <p><small>{{ holding.company_sector }}</small></p>
          <div class="level">
            <div class="level-left">
              <div class="level-item holding-controls">
                <b-tooltip
                  label="Find similar holdings to replace this one"
                  position="is-right"
                >
                  <b-icon
                    v-on:click.native="exchangeHolding(holding)"
                    pack="fas"
                    icon="exchange-alt"
                  />
                </b-tooltip>
              </div>
            </div>
            <div class="level-item">
              <b-tooltip
                label="Unlock weight"
                v-if="holding.locked"
              >
                <b-icon
                  v-if="holding.locked"
                  v-on:click.native="() => holding.locked = false"
                  pack="fas"
                  icon="lock"
                />
              </b-tooltip>
              <b-tooltip
                label="Lock weight to prevent auto adjustment"
                v-if="!holding.locked"
              >
                <b-icon
                  v-if="!holding.locked"
                  v-on:click.native="() => holding.locked = true"
                  pack="fas"
                  icon="lock-open"
                />
              </b-tooltip>
              &nbsp;
              <b-input
                type="number"
                label="Weight"
                v-bind:value="holding.percentage"
                v-bind:disabled="holding.locked || portfolio.holdings.length === 1"
                v-bind:min="0"
                v-bind:max="100"
                v-on:blur="adjustPercentages(holding, $event)"
              />
              &nbsp;
              <b-tooltip
                label="Remove this holding"
              >
                <b-icon
                  v-on:click.native="removeHolding(index)"
                  pack="fas"
                  icon="trash"
                />
              </b-tooltip>
            </div>
            <div
              class="level-item overall-contribution"
              v-if="screenedHoldingsById[holding.id] && aggregateScore('composite')"
            >
              <p v-if="!usingPercentiles">
                <small>Vs.<br>Overall</small><br>
                {{ Math.round(screenedHoldingsById[holding.id].composite - aggregateScore('composite')) > 0 ? '+' : ''}}
                {{ Math.round(screenedHoldingsById[holding.id].composite - aggregateScore('composite')) }}
              </p>
            </div>
            <div class="level-item">
              <Holding
                v-bind:ticker="holding"
                v-bind:screened="screenedHoldingsById[holding.id]"
                v-bind:values="values"
                v-bind:screens="screens"
                v-bind:chartValueRange="chartValueRange"
              />
            </div>
          </div>
        </li>
      </ul>
      <p v-if="portfolio.holdings.length === 0">
        Add holdings from the screener results tab.
      </p>
    </div>
  </div>
</template>

<script>
  import chroma from 'chroma-js';
  import _ from 'lodash';

  import ValuesChart from '@frontend/dash3/js/components/fundscreener/ValuesChart.js';
  import Holding from '@frontend/dash3/js/components/fundscreener/Holding.vue';
  import TypeaheadPortfolioSearch from '@frontend/dash3/js/components/portfolios/TypeaheadPortfolioSearch.vue';
  import IRebalSync from '@frontend/dash3/js/components/integrations/irebal/IRebalSync.vue';

  import eventBus from '@frontend/shared/EventBus.js';
  import {preciseRound} from '@frontend/shared/Util.js';

  export default {
    data() {
      return {
        portfolio: {
          name: 'Untitled Portfolio',
          holdings: [],
          screenedHoldings: [],
          apiObject: null,

          destination: {
            id: null,
            status: 'd',
          },
          holdingPercentages: {},
        },
        saving: null,
        edited: false,

        exchangeTarget: null,

        weightsManuallyAdjusted: false,

        holdingsSort: {
          key: null,
          dir: 'asc',
        },
      }
    },
    components: {
      TypeaheadPortfolioSearch,
      ValuesChart,
      Holding,
      IRebalSync,
    },
    props: {
      values: {
        type: Object,
        default() {
          return {
            a: [],
            byId: {},
          }
        }
      },
      screens: {
        type: Object,
        default() {
          return {
            a: [],
            byId: {},
          }
        }
      },
      funds: {
        type: Boolean,
        default: false,
      },
      companies: {
        type: Boolean,
        default: false,
      },
      chartValueRange: {
        type: Array,
        default() {
          return [0, 10]
        }
      }
    },
    methods: {
      aggregateScore: function(column) {
        const holdingPcts = this.holdingPercentagesById;
        const weightedScores = this.portfolio.screenedHoldings.map((itm) => {
          return itm[column] * (holdingPcts[itm.ticker__id]);
        });
        if (weightedScores.length > 0) {
          return weightedScores.reduce((a, b) => { return a + b; }) / this.portfolio.screenedHoldings.reduce((a, b) => { return a + holdingPcts[b.ticker__id] }, 0);
        }
        return '';
      },
      clear: function() {
        this.saving = null;
        this.portfolio.name = 'Untitled Portfolio';
        this.portfolio.holdings = [];
        this.portfolio.screenedHoldings = [];
        this.portfolio.apiObject = null;
        this.portfolio.destination.id = null;
        this.portfolio.destination.status = 'd';
        this.portfolio.holdingPercentages = {};
        this.portfolio.originalId = null;
        this.portfolio.originalValue = null;
        this.weightsManuallyAdjusted = false;
        this.exchangeTarget = null;
        this.edited = false;
        eventBus.$emit('FundScreener:exchange-holding', null);
      },
      preciseRound: function(val, dig) {
        return preciseRound(val, dig);
      },
      doSave: async function(name) {
        this.saving = true;
        if (this.portfolio.destination.status === 'd' && !this.edited) {
          this.edited = true;
          this.portfolio.name = this.portfolio.name + ' (Draft Copy)';
        }
        console.log('[WorkingPortfolio] Save initiated.');
        const url = this.portfolio.destination.id ? `/api/v1/portfolios/${this.portfolio.destination.id}/` : '/api/v1/portfolios/';
        let req = await fetch(url, {
          method: this.portfolio.destination.id ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            'name': name || this.portfolio.name,
            'status': this.portfolio.destination.status,
            'total_value': this.portfolio.originalValue ? this.portfolio.originalValue : 1000000,
            'holdings': this.portfolio.holdings.map((itm) => {
              return {
                'identifier': itm.id,
                'identifier_type': 'ticker_id',
                'percentage': itm.percentage / 100,
            }})
          }),
        })
        if (req.ok) {
          let data = await req.json();
          console.log('Saved portfolio');
          if (name) {
            this.portfolio.name = name;
          }
          this.portfolio.destination.id = data.id;
          this.saving = false;
          return data;
        }
        else {
          console.log('Error saving portfolio');
        }
      },
      removeHolding: function(index) {
        const id = this.portfolio.holdings[index].id;
        // We are managing holdings in two places because the fund screener doesn't necesarily return everything
        // that makes up a portfolio, so if you are editing a mixed portfolio we want to ensure that you don't save a
        // copy missing the holdings that aren't returned by the fund screener.
        // This dance in particular is necessary because the lists may not be sorted the same way, so we delete directly
        // by index from the list used in rendering the list of holdings, while searching the other list before deleting.
        for (let i = 0; i < this.portfolio.screenedHoldings.length; i++) {
          if (this.portfolio.screenedHoldings[i].ticker.id === id) {
            this.portfolio.screenedHoldings.splice(i, 1);
            break;
          }
        }
        this.portfolio.holdings.splice(index, 1);
        this.doSave();
      },
      savePortfolio: async function() {
        this.$buefy.dialog.prompt({
          message: "Save this portfolio as:",
          inputAttrs: {
              placeholder: this.portfolio.name,
              maxlength: 200,
              value: this.portfolio.name,
          },
          trapFocus: true,
          closeOnConfirm: false,
          confirmText: 'Save',
          onConfirm: async (value, {close}) => {
            this.$buefy.toast.open(`Saving as: ${value}...`);
            this.portfolio.destination.status = 'a';
            this.portfolio.destination.id = null;
            let result = await this.doSave(value);
            eventBus.$emit('FundScreener:working-portfolio-selected', {name: this.portfolio.name});
            close();
          }
        });
      },
      showDetail: function(holding, event) {
        event.preventDefault();
        eventBus.$emit('FundScreener:show-detail', this.screenedHoldingsById[holding.id]);
      },
      holdingClass: function(holding) {
        let classes = ['holding-row'];
        if (this.exchangeTarget && holding.id === this.exchangeTarget.id) {
          classes.push('exchange-target');
        }
        if (!(holding.id in this.screenedHoldingsById)) {
          classes.push('not-applicable');
        }
        return classes.join(' ');
      },
      applyScreens: async function(firstLoad) {
        this.portfolio.screenedHoldings = [];

        if (this.companies) {
          this.isLoading = true;
          let thresholds = {}
          for (let i in this.screens.a) {
            let thisScreen = this.screens.a[i];
            if (thisScreen.userPercentile && thisScreen.userPercentile > 0) {
              thresholds[`m_${thisScreen.id}`] = thisScreen.userPercentile / 100;
            }
          }
          if (Object.keys(thresholds).length === 0) {
            this.isLoading = false;
            this.excludedCount = 0;
            this.includedCount = 0;
            return;
          }
          let params = new URLSearchParams(thresholds);

          fetch(`/api/v1/impact_factors/screen/model_portfolio/${this.portfolio.originalId}/?${params}`)
            .then((data) => {return data.json()})
            .then((data) => {
              this.results = data.results;
              this.portfolio.screenedHoldings.push(...data.results);
            });
        }

        if (this.funds) {
          let params = new URLSearchParams({
            values: this.values.a.map((itm) => { return itm.id }).join(','),
            in_fund_ids: this.portfolio.holdings
              .filter((itm) => { return itm.asset_type === 'ETF' || itm.asset_type === 'Mutual Fund' })
              .map((itm) => { return itm.id }).join(','),
            per_page: 99999,
            fund_types: '__ALL__',
          })
          let fundsResponse = await fetch(`/api/v1/explore/funds/?${params}`);
          let fundsData = await fundsResponse.json();
          this.portfolio.screenedHoldings.push(...fundsData.funds);
        }
        if (firstLoad) {
          this.portfolio.holdings.sort((a, b) => {
            if ((a.id in this.screenedHoldingsById) && (b.id in this.screenedHoldingsById)) {
              return b.percentage - a.percentage;
            }
            if (a.id in this.screenedHoldingsById) {
              return -1;
            }
            return 1;
          })
        }
      },
      portfolioSelectedCallback: async function(option) {
        this.weightsManuallyAdjusted = true;
        this.portfolio.destination.id = null;
        this.portfolio.name = option.name;
        this.portfolio.originalId = option.id;
        if (this.portfolio.holdings.length > 0) {
          this.portfolio.holdings = [];
        }
        let response = await fetch(`/api/v1/portfolios/${option.id}/?holdings=true`);
        let data = await response.json();

        let totalValue = data.portfolio.holdings.reduce((acc, itm) => {
          return acc + itm.dollars;
        }, 0);

        this.portfolio.originalValue = totalValue;

        data.portfolio.holdings.forEach((itm) => {
          itm.ticker.locked = false;
          itm.ticker.percentage = preciseRound((itm.dollars / totalValue) * 100, 2);
        })

        const fundAssetTypes = {'ETF': true, 'Mutual Fund': true};
        this.portfolio.holdings.push(
            ...data.portfolio.holdings
            .map((itm) => { return itm.ticker; })
        );

        this.portfolio.apiObject = data.portfolio;

        eventBus.$emit('FundScreener:working-portfolio-selected', {name: this.portfolio.name});
        this.applyScreens(true);
      },
      exchangeHolding: function(holding) {
        if (this.exchangeTarget && holding.id === this.exchangeTarget.id) {
          this.exchangeTarget = null;
          eventBus.$emit('FundScreener:exchange-holding', null);
          eventBus.$emit('CompanyScreener:exchange-holding', null);
        }
        else {
          this.exchangeTarget = holding;
          // Use screenedHoldingsById if available, otherwise create a minimal object from holding
          const holdingData = this.screenedHoldingsById[holding.id] || {
            ticker__id: holding.id,
            ticker__symbol: holding.symbol,
            ticker__full_name: holding.full_name,
            ticker__benchmark_description: holding.benchmark_description,
            ticker__asset_type: holding.asset_type,
            ticker__expense_ratio: holding.expense_ratio,
            ticker__total_value: holding.total_value,
            ticker__esg_mandate: holding.esg_mandate
          };
          eventBus.$emit('FundScreener:exchange-holding', holdingData);
          eventBus.$emit('CompanyScreener:exchange-holding', {
            metrics: holdingData,
            holding: holding,
          });
        }
      },
      colorScale: function() {
        return chroma.scale(['red', 'white', 'lightgreen']).domain(this.chartValueRange);
      },
      sumPct: function() {
        return this.portfolio.holdings.reduce((a, b) => { return a + b.percentage}, 0);
      },
      adjustPercentages: function(changedHolding, event) {
        const newNumber = Number(event.target.value);
        this.weightsManuallyAdjusted = true;
        if (newNumber + this.lockedTotal > 100) {
          // Unable to resolve a case where the changed slider + all locked sliders add up to more than 100, so we set
          // the slider
          changedHolding.percentage = 100 - this.lockedTotal;
        }
        else {
          changedHolding.percentage = newNumber;
        }

        let difference = preciseRound(100 - this.sumPct(), 2);
        let iter = 0;
        let factor = difference;
        while (Math.abs(difference, 2) > 0 && Math.abs(factor) > 0 && iter < 20) {
          // Very simply redistribute the percentage points that we're over/under 100 after applying the slider change.
          // We just evenly spread the difference out until it is negligible. This is non optimal since some values will
          // go to zero along the way, so instead of getting fance we just make a few passes. 20 pass limit in case of
          // unexpected values so we don't lock up the browser with an inifinite loop.
          iter++;

          // TODO: Slightly weird behavior with numbers around 0/100 introduced by rounding

          console.log(`Iteration #${iter}, diff=${difference}`);
          let holdingsToChange = this.portfolio.holdings.filter((itm) => {
            return itm.id !== changedHolding.id && !itm.locked
          });

          factor = preciseRound(difference / holdingsToChange.length, 2);
          holdingsToChange.forEach((itm) => {
            let newPct = preciseRound(itm.percentage + factor, 2);
            if (newPct <= 100 && newPct >= 0) {
              itm.percentage = preciseRound(newPct, 2);
            }
          });
          difference = preciseRound(100 - this.sumPct(), 2);
        }

        this.debouncedSave();
      },
      chartClick: function(evt, target, chart) {
        if (target.length > 0 && target[0].index >= 0) {
          const sortKey = chart.data.sortKeys[target[0].index];
          if (this.holdingsSort.key === sortKey) {
            this.holdingsSort.dir = this.holdingsSort.dir === 'asc' ? 'desc' : 'asc';
          }
          else {
            this.holdingsSort.dir = 'asc';
          }
          this.holdingsSort.key = sortKey;
          this.portfolio.holdings.sort((a, b) => {
            if ((a.id in this.screenedHoldingsById) && (b.id in this.screenedHoldingsById)) {
              let aVal = this.screenedHoldingsById[b.id][sortKey],
                  bVal = this.screenedHoldingsById[a.id][sortKey];
              return this.holdingsSort.dir === 'asc' ? aVal - bVal : bVal - aVal;
            }
            if (a.id in this.screenedHoldingsById) {
              return -1;
            }
            return 1;
          });
        }
      },
    },
    computed: {
      usingPercentiles: function() {
        return this.screens.a.length > 0;
      },
      holdingPercentagesById: function() {
        return this.portfolio.holdings
          .reduce((acc, itm) => {
            acc[itm.id] = itm.percentage;
            return acc;
          }, {});
      },
      screenedHoldingsById: function() {
        return this.portfolio.screenedHoldings.reduce((acc, itm) => {
          acc[itm.ticker__id] = itm;
          return acc
        }, {});
      },
      lockedTotal: function() {
        return this.portfolio.holdings.filter((itm) => {
          return itm.locked;
        }).reduce((a, b) => { return a + b.percentage }, 0);
      },
      hasWorkingPortfolio: function() {
        return this.portfolio.apiObject || this.portfolio.destination.id;
      },
      chartOptions: function() {
        return {
          onClick: this.chartClick,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              grid: {
                display: false,
                drawBorder: false,
                color: 'hotpink'
              },
              display: false,
              min: 0,
              max: this.usingPercentiles ? 100 : 10,
              stepSize: 1
            }
          }
        }
      },
      chartData: function() {
        let overall = this.aggregateScore('composite');
        if (this.usingPercentiles) {
          overall = overall * 100; // If we're running on screens instead of values, scores are percentelies
        }
        let color = this.colorScale()(overall);
        let overallLabel = this.usingPercentiles ? 'Average Percentile': 'Overall Alignment';
        let data = {
          labels: [overallLabel],
          sortKeys: ['composite'],
          datasets: [
            {
              backgroundColor: [color.hex()],
              hoverBackgroundColor: [color.hex()],
              borderColor: [chroma(color).darken().hex()],
              borderWidth: 1,
              data: [preciseRound(overall, 1)],
            }
          ]
        }
        this.values.a.forEach((itm) => {
          data.labels.push(itm.name);
          data.sortKeys.push(`composite_values_${itm.id}`);
          let score = this.aggregateScore(`composite_values_${itm.id}`);
          let color = this.colorScale()(score).hex();
          data.datasets[0].backgroundColor.push(color);
          data.datasets[0].hoverBackgroundColor.push(color);
          data.datasets[0].borderColor.push(chroma(color).darken().hex());
          data.datasets[0].data.push(preciseRound(score, 1));
        });

        this.screens.a.filter((itm) => { return itm.userPercentile > 0 }).forEach((itm) => {
          data.labels.push(itm.name);
          data.sortKeys.push(`metric_${itm.id}_percentile`);
          let score = this.aggregateScore(`metric_${itm.id}_percentile`) * 100;
          let color = this.colorScale()(score).hex();
          data.datasets[0].backgroundColor.push(color);
          data.datasets[0].hoverBackgroundColor.push(color);
          data.datasets[0].borderColor.push(chroma(color).darken().hex());
          data.datasets[0].data.push(preciseRound(score, 1));
        });
        return data;
      },
      reportUrl: function() {
        let args = {
          reporting: this.portfolio.destination.id,
          values: this.values.a.map((itm) => itm.id).join(','),
          reportType: 'impact',
          reportStyle: 'minimal_relative',
        };
        if (this.portfolio.originalId) {
          args.benchmark = this.portfolio.originalId;
        }
        return `/ui/advisor/reports/?${new URLSearchParams(args)}`
      }
    },
    created: function() {
      const self = this;

      self.debouncedSave = _.debounce(this.doSave, 1000);

      eventBus.$on('FundScreener:values-changed', () => {
        if (self.portfolio.holdings.length > 0) {
          this.applyScreens();
        }
      });

      // Clear exchange target when exchange mode is cancelled from main view
      eventBus.$on('FundScreener:exchange-holding', (holding) => {
        if (!holding) {
          self.exchangeTarget = null;
        }
      });

      eventBus.$on('CompanyScreener:add-holding-to-portfolio', (holdings, mode) => {
        const holdingsCopy = JSON.parse(JSON.stringify(holdings));
        const knownIds = this.portfolio.holdings.reduce((acc, itm) => { acc[itm.id] = true; return acc; }, {});
        const dedupedFunds = holdingsCopy.filter((itm) => { return !(itm.ticker__id in knownIds); });

        if (mode === 'exchange' && !this.exchangeTarget) {
          return;
        }
        let percentage = self.exchangeTarget.percentage;

        dedupedFunds.forEach((itm) => {
          itm.ticker = {
            id: itm.ticker__id,
            full_name: itm.name,
            symbol: itm.symbol,
            percentage: percentage,
            locked: false
          }
        });

        if (dedupedFunds.length === 0) {
          this.$buefy.dialog.alert(
            `${holdingsCopy[0].symbol} is already in this portfolio, please select a different company`
          );
          self.exchangeTarget = null;
          return;
        }
        let spliceIndex = self.portfolio.holdings.map((itm) => { return itm.id }).indexOf(self.exchangeTarget.id);
        if (spliceIndex !== -1) {
          self.portfolio.holdings.splice(
            spliceIndex,
            1,
            dedupedFunds[0].ticker
          );
          self.portfolio.screenedHoldings.splice(
            self.portfolio.screenedHoldings.map((itm) => { return itm.ticker__id }).indexOf(self.exchangeTarget.id),
            1,
            dedupedFunds[0]
          );
        }
        self.exchangeTarget = null;
        self.$nextTick(() => {
          self.debouncedSave();
        });
      });

      eventBus.$on('FundScreener:add-funds-to-portfolio', (funds, mode) => {
        const fundsCopy = JSON.parse(JSON.stringify(funds));
        
        // Transform fund screener data structure to match portfolio expectations
        fundsCopy.forEach(fund => {
          if (fund.ticker__id && (typeof fund.ticker !== 'object' || fund.ticker === null)) {
            // Transform from flattened structure (ticker__property) to nested structure (ticker.property)
            fund.ticker = {
              id: fund.ticker__id,
              symbol: fund.ticker__symbol,
              full_name: fund.ticker__full_name,
              benchmark_description: fund.ticker__benchmark_description,
              expense_ratio: fund.ticker__expense_ratio,
              total_value: fund.ticker__total_value,
              esg_mandate: fund.ticker__esg_mandate
            };
            
            // Preserve composite and values data 
            if (fund.composite !== undefined) {
              fund.ticker.composite = fund.composite;
            }
            
            // Preserve individual composite_values scores
            Object.keys(fund).forEach(key => {
              if (key.startsWith('composite_values_')) {
                fund.ticker[key] = fund[key];
              }
            });
          }
        });
        const knownIds = this.portfolio.holdings.reduce((acc, itm) => { acc[itm.id] = true; return acc; }, {});
        const dedupedFunds = fundsCopy.filter((itm) => { return !(itm.ticker.id in knownIds); });

        if (mode === 'exchange' && !this.exchangeTarget) {
          return;
        }

        let percentage = 0;
        if (mode === 'exchange') {
          percentage = self.exchangeTarget.percentage;
        }
        else {
          if (this.portfolio.holdings.length === 0) {
            percentage = 100 / dedupedFunds.length;
          }
          else if (!this.weightsManuallyAdjusted) {
            percentage = 100 / (this.portfolio.holdings.length + dedupedFunds.length);
            this.portfolio.holdings.forEach((itm) => {
              itm.percentage = percentage;
            })
          }
        }
        dedupedFunds.forEach((itm) => {
          itm.ticker.percentage = percentage;
          itm.ticker.locked = false;
        })
        if (dedupedFunds.length === 0) {
          this.$buefy.dialog.alert(
            `${funds[0].ticker.symbol} is already in this portfolio, please select a different company`
          );
          self.exchangeTarget = null;
          return;
        }
        if (mode === 'exchange') {
          let spliceIndex = self.portfolio.holdings.map((itm) => { return itm.id }).indexOf(self.exchangeTarget.id);
          if (spliceIndex !== -1) {
            self.portfolio.holdings.splice(
              spliceIndex,
              1,
              dedupedFunds[0].ticker
            );
            self.portfolio.screenedHoldings.splice(
              self.portfolio.screenedHoldings.map((itm) => { return itm.ticker.id }).indexOf(self.exchangeTarget.id),
              1,
              dedupedFunds[0]
            );
          }
          self.exchangeTarget = null;
        }
        else {
          self.portfolio.holdings.unshift(...dedupedFunds.map((itm) => { return itm.ticker }));
          self.portfolio.screenedHoldings.push(...dedupedFunds);
        }
        self.$nextTick(() => {
          self.debouncedSave();
        });
      })
    }
  }
</script>

<style lang="scss">
.working-portfolio {
  min-height: 400px;
}

.not-applicable {
  opacity: 0.5;
}

.holdings {
  .holding-controls {
    min-width: 50px;
    justify-content: left;
  }

  .holding-row {
    padding: 1rem;
  }

  .holding-row:nth-child(even) {
    background-color: #fbfbfb;
  }

  .holding-row.exchange-target {
    border-left: 5px solid hotpink;
  }

  .overall-contribution {
    text-align: center;
  }
}

</style>
